import { z } from 'zod';
import { ElementFingerprintSchema } from './element-fingerprint';

/**
 * No-code program · Phase 11 · Next-action suggestions.
 *
 * Three surfaces live in this file:
 *
 * 1. `OutlinePageRequest` / `OutlinePageResponse` — wire shape of the
 *    new `outline-page` WebSocket message handled by the mvp runner.
 *    The runner does ONE `page.evaluate` per scan that returns the top
 *    interactive elements, forms, and nav links — pre-scored with the
 *    same locator strategies the live element inspector already uses.
 *    The editor then slices that into 5 basic-action chips + up to 3
 *    smart-action chips entirely client-side, no extra round trip.
 *
 * 2. `SuggestActionsPolishRequest` / `SuggestActionsPolishResponse` —
 *    contract for `POST /api/suggested-actions/polish`. Takes the
 *    visible chip labels and returns nicer English in ONE LLM call
 *    per scan (Groq free tier by default), cached. Optional — the
 *    chips render immediately from the rule-based labels and the
 *    polished labels swap in when the call resolves.
 *
 * 3. `SuggestActionsTelemetryEvent` — payload for
 *    `POST /api/suggested-actions/telemetry`. Lightweight audit-log
 *    writer that records `flow.suggest.shown` and
 *    `flow.suggest.accepted` rows so admins can measure adoption
 *    without standing up PostHog. Schema is intentionally tiny.
 *
 * Why one file: all three surfaces are tightly coupled — the editor
 * computes outline → builds suggestions → optionally polishes →
 * telemetry-logs every shown / accepted event. Keeping the contracts
 * together means a future change to the suggestion shape only has to
 * touch one Zod tree.
 */

// ---- Outline element ----------------------------------------------------

/** Coarse classification of a captured DOM element. The editor's
 *  build-suggestions libs branch on this to decide whether the element
 *  becomes a `click`, `type`, `select`, `assert`, or `hover` chip. */
export const OutlineElementKindSchema = z.enum([
  'button',
  'link',
  'input',
  'textarea',
  'select',
  'heading',
  'image',
  'other',
]);
export type OutlineElementKind = z.infer<typeof OutlineElementKindSchema>;

/**
 * One interactive element captured by the runner walker. The same
 * `selector / strategy / confidence / matches / pickedTag / pickedText`
 * shape the existing inspect-handler emits — extended with the kind
 * and inputType fields the suggestion builder needs to bucket the
 * element into the right action.
 */
export const OutlineElementSchema = z.object({
  kind: OutlineElementKindSchema,
  /** HTML input `type` attribute when relevant ('email', 'password',
   *  'search', …). Empty string for non-inputs. */
  inputType: z.string().optional(),
  /** Visible text, aria-label, or placeholder — first non-empty wins.
   *  Truncated to 80 chars so an entire-paragraph button doesn't blow
   *  up the WS payload. */
  text: z.string().optional(),
  /** ARIA role hint inferred from the tag/role attribute. */
  role: z.string().optional(),
  /** Best selector picked by the shared scorer. */
  selector: z.string().min(1),
  /** Strategy name ('Test ID', 'Role + Name', …). Same vocabulary as
   *  the inspector for consistent badge rendering. */
  strategy: z.string().min(1),
  /** 0-100. Same rubric as `inspect-handler.ts`. */
  confidence: z.number().min(0).max(100),
  /** Live `document.querySelectorAll(selector).length` at scan time. */
  matches: z.number().int().nonnegative(),
  /** Tag of the captured element ('button', 'input', …). */
  pickedTag: z.string().min(1),
  /** Preserved for the chip's friendly preview (mirrors the inspector). */
  pickedText: z.string().optional(),
  /**
   * Phase 11.4 (2026-05-26) — ARIA landmark of the nearest enclosing
   * region, when one exists. Computed runner-side via `el.closest(...)`
   * over the standard landmark roles + their semantic tag equivalents
   * (`main` / `[role=main]`, `nav` / `[role=navigation]`, `header` /
   * `[role=banner]`, `footer` / `[role=contentinfo]`, `aside` /
   * `[role=complementary]`, `form` / `[role=search]` / `[role=form]`).
   *
   * Used by the editor's grouped-suggestion panel (layoutVersion `v2`)
   * to cluster element rows under their page region — "Main content",
   * "Header", "Footer", etc. — so a Search page collapses from a flat
   * list of ~25 elements into ~4 small regional groups.
   *
   * Optional + missing means "no enclosing landmark found" — the UI
   * buckets such elements into an "Other" group rather than dropping
   * them. Older runner images that never compute this field also
   * gracefully degrade to the "Other" bucket.
   */
  landmark: z
    .enum(['main', 'navigation', 'banner', 'contentinfo', 'complementary', 'search', 'form', 'region'])
    .optional(),
  /**
   * Phase 11.5 (2026-05-26) — current `el.value` for `input` /
   * `textarea` / `select` elements at scan time, truncated to 80
   * chars. Captured runner-side in `dom-scripts.ts` inside a
   * try/catch (some custom-element implementations throw on
   * `.value` access; the field stays undefined on failure).
   *
   * Consumed by the editor's `VerifyAssertionPicker` (v3 layout)
   * to pre-fill the `value-equals` assertion's expected-value
   * input — so picking "value equals" on a search box that already
   * shows "iphone 15" puts "iphone 15" in the input automatically;
   * the user presses Enter to commit or overwrites first.
   *
   * Undefined on non-input elements, on inputs whose `.value`
   * throws, and on older runner images that don't compute this
   * field. The editor gracefully falls back to an empty input.
   */
  value: z.string().max(80).optional(),
  /**
   * Locator Strategy Overhaul · Phase 0 (2026-06-08) — full ranked
   * candidate list for the picked element. Mirrors
   * `FlowStepLocatorCandidate` from `packages/mvp/src/types.ts` so the
   * editor can persist them straight into `step.fields.locatorCandidates`
   * (the JSON-encoded chain the runner reads back as
   * `step.recordedCandidates` and races at Tier -1 before any DOM
   * walking). Carrying the chain through Build Mode picks (outline scan,
   * find-by-text search, on-page hover) means a freshly-recorded step
   * already has 4-8 fallback selectors before its first run — closing
   * the long-standing gap where pick-mode steps had a single brittle
   * selector while recording-mode steps had a self-healing chain.
   *
   * Trimmed to the top 8 by confidence so the WS payload stays bounded
   * even on pages with rich locator surfaces. Optional + missing
   * gracefully degrades to the legacy single-selector behaviour for
   * old runner images.
   */
  candidates: z
    .array(
      z.object({
        kind: z.string().min(1),
        selector: z.string().min(1),
        confidence: z.number().min(0).max(100),
        reason: z.string().optional(),
      }),
    )
    .optional(),
  /**
   * Locator Strategy Overhaul · Phase 2 (2026-06-08) — full element
   * fingerprint captured at scan time. See
   * `packages/contracts/src/element-fingerprint.ts` for the wire
   * shape; the editor persists this verbatim onto
   * `step.fields.elementFingerprint` so the runtime resolver can
   * score live DOM candidates against it when every selector misses.
   * Optional + missing degrades gracefully — older runner images
   * keep working without this signal.
   */
  fingerprint: ElementFingerprintSchema.optional(),
});
export type OutlineElement = z.infer<typeof OutlineElementSchema>;

// ---- Outline form (smart-action source) ---------------------------------

/** One field inside a captured form. */
export const OutlineFormFieldSchema = z.object({
  name: z.string(),
  type: z.string(),
  label: z.string(),
  placeholder: z.string(),
  required: z.boolean(),
});
export type OutlineFormField = z.infer<typeof OutlineFormFieldSchema>;

/**
 * One form captured by the runner. Used by the smart-action builder
 * to detect Login / Register / Search / Checkout patterns and emit a
 * single composite step that the runtime expander unfolds. Mirrors
 * the existing `AppCrawler.extractForms` shape so we share the
 * extraction code with the CLI.
 */
export const OutlineFormSchema = z.object({
  /** Selector of the form element itself. */
  selector: z.string().min(1),
  fields: z.array(OutlineFormFieldSchema),
  submitButtonText: z.string(),
  /** Heuristic classification of the form. Set client-side from the
   *  field signature; the runner reports the raw form and the
   *  build-smart-suggestions lib decides which bucket it belongs to. */
  smartActionHint: z.enum(['login', 'register', 'search', 'checkout']).nullable().optional(),
});
export type OutlineForm = z.infer<typeof OutlineFormSchema>;

// ---- Outline link (smart-nav source) ------------------------------------

/** One nav link captured by the runner. Used today only as input to
 *  smart-action heuristics; future iterations may surface "Click <nav
 *  item>" as a basic chip too. */
export const OutlineLinkSchema = z.object({
  text: z.string(),
  href: z.string(),
  /** True when the link sits inside `<nav>`, `<header>`, or
   *  `[role='navigation']`. */
  isNavigation: z.boolean(),
  selector: z.string(),
});
export type OutlineLink = z.infer<typeof OutlineLinkSchema>;

// ---- Outline request / response (WebSocket) -----------------------------

export const OutlinePageRequestSchema = z.object({
  type: z.literal('outline-page'),
  /** Optional request id so the editor can correlate concurrent scans
   *  if a navigation lands while a scan is in flight. */
  requestId: z.string().optional(),
  /** Cap on captured interactive elements. The runner clamps this to
   *  [1, 50] so a buggy editor can't ask for the entire DOM. Default 25. */
  maxElements: z.number().int().positive().max(50).optional(),
});
export type OutlinePageRequest = z.infer<typeof OutlinePageRequestSchema>;

export const OutlinePageResponseSchema = z.object({
  type: z.literal('outline-result'),
  requestId: z.string().optional(),
  ok: z.boolean(),
  error: z.string().optional(),
  /** Page URL at scan time, echoed back so the editor can reject a
   *  stale response that arrived after the user navigated again. */
  url: z.string(),
  /** Sorted by confidence desc. Capped per request (default 25). */
  elements: z.array(OutlineElementSchema).default([]),
  /** Capped at 10. */
  forms: z.array(OutlineFormSchema).default([]),
  /** Capped at 50. */
  links: z.array(OutlineLinkSchema).default([]),
});
export type OutlinePageResponse = z.infer<typeof OutlinePageResponseSchema>;

// ---- Pick-by-label (Build-mode label search) ----------------------------

/**
 * `pending-on-page-build-mode` v0.2 — "Pick by label" search.
 *
 * The on-page hover model breaks down when the target is below the
 * fold of a scrollable drawer (Amazon hamburger menu), inside a
 * virtualised list, or simply not visible from where the user is
 * looking. The label-search path is viewport-agnostic: the runner
 * walks the full DOM, filters elements whose visible text /
 * accessible label contains the query, scores them with the same
 * `__rynoraScoreElement` pipeline as `inspect-element`, and returns
 * ranked matches. The editor picks one + verb in a single keystroke
 * chord, no hover / scroll dance.
 *
 * Wire shape mirrors `outline-page` / `outline-result` — same
 * `OutlineElement` payload so the existing `buildModeStep` adapter
 * commits these picks identically to side-strip chip-accept.
 */
export const FindElementsByTextRequestSchema = z.object({
  type: z.literal('find-elements-by-text'),
  /** User-supplied text fragment. Case-insensitive, whitespace
   *  collapsed; matched against visible text, aria-label, name,
   *  placeholder, title, and image alt. 1..200 chars to keep
   *  the runner script payload tame. */
  query: z.string().min(1).max(200),
  /** Optional request id so the editor can correlate the response
   *  with the latest keystroke (mid-flight responses for stale
   *  queries are discarded). */
  requestId: z.string().optional(),
  /** Max matches to return. The runner clamps to [1, 30] — 30 is
   *  the largest dropdown we render before the find-bar's
   *  scrollable result list takes over. Default 12. */
  limit: z.number().int().positive().max(30).optional(),
});
export type FindElementsByTextRequest = z.infer<typeof FindElementsByTextRequestSchema>;

/** Match-quality bucket — the runner emits this so the editor can
 *  group / colour-code matches without re-scoring on the client.
 *  Higher buckets win ties on confidence. */
export const FindElementsMatchQualitySchema = z.enum([
  'exact', // textContent equals query (case-insensitive, trim)
  'prefix', // textContent starts with query
  'word', // query falls on a word boundary inside textContent
  'contains', // anywhere inside textContent
]);
export type FindElementsMatchQuality = z.infer<typeof FindElementsMatchQualitySchema>;

/** One match. Reuses `OutlineElement` so the editor's commit path
 *  (`outlineElementFromInspect` ↔ `buildModeStep`) treats label
 *  picks and hover picks identically. The extra fields capture the
 *  spatial + ranking metadata the find-bar UI needs without
 *  another round-trip:
 *    - `matchQuality` / `matchedField` for grouping + tooltips
 *    - `rect` so the editor can paint Vimium-style hint markers
 *      in the same response (phase C — currently optional)
 *    - `hintLabel` — short letter(s) (a, b, …, aa, ab) assigned in
 *      rank order; the user can type it instead of clicking. */
export const FindElementsMatchSchema = z.object({
  element: OutlineElementSchema,
  matchQuality: FindElementsMatchQualitySchema,
  matchedField: z.enum(['text', 'ariaLabel', 'name', 'placeholder', 'title', 'alt', 'value']),
  /** Bounding box in viewport coordinates AT SCAN TIME. May go
   *  stale if the page reflows; the editor re-resolves via
   *  `highlight-element` (which auto-scrolls) before painting. */
  rect: z
    .object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
  hintLabel: z.string().min(1).max(4),
});
export type FindElementsMatch = z.infer<typeof FindElementsMatchSchema>;

export const FindElementsByTextResponseSchema = z.object({
  type: z.literal('find-elements-by-text-result'),
  requestId: z.string().optional(),
  ok: z.boolean(),
  error: z.string().optional(),
  /** Echo of the request query so a delayed response for an old
   *  keystroke can be detected + dropped on the editor side. */
  query: z.string(),
  /** Page URL at scan time — same staleness check as outline. */
  url: z.string(),
  /** Sorted by (matchQuality bucket, confidence desc, rank). */
  matches: z.array(FindElementsMatchSchema).default([]),
  /** Total number of elements that matched before the `limit`
   *  cut-off. The editor uses this to render a "+12 more — refine
   *  your search" affordance. */
  totalMatches: z.number().int().nonnegative(),
});
export type FindElementsByTextResponse = z.infer<typeof FindElementsByTextResponseSchema>;

// ---- Scroll-to-element (find-bar auto-tracking) ------------------------
//
// `pending-on-page-build-mode` v0.2.6 — dedicated scroll handler.
//
// Previously the find-bar relied on the highlight handler's
// best-effort `scrollIntoView` to pull below-the-fold matches
// into view. That worked sometimes (instant scroll, sync rect
// measurement) but never with the certainty a user types in the
// find bar expecting the page to follow. This contract decouples
// scrolling from highlighting: the find-bar fires a guaranteed,
// smoothly-animated scroll the moment matches arrive AND every
// time the active row changes, so the page tracks the search in
// real time without any click / arrow interaction.

export const ScrollToElementRequestSchema = z.object({
  type: z.literal('scroll-to-element'),
  selector: z.string().min(1),
  requestId: z.string().optional(),
  /** Hint to the runner: `'smooth'` animates (visible to user),
   *  `'auto'` jumps instantly (rect-safe for follow-up highlight
   *  if the caller plans to read a post-scroll rect). Defaults to
   *  `'smooth'` because find-bar typing is the primary caller and
   *  the user explicitly wants to see the page move. */
  behavior: z.enum(['auto', 'smooth']).optional(),
});
export type ScrollToElementRequest = z.infer<typeof ScrollToElementRequestSchema>;

export const ScrollToElementResponseSchema = z.object({
  type: z.literal('scroll-to-element-result'),
  requestId: z.string().optional(),
  ok: z.boolean(),
  error: z.string().optional(),
  /** True when the element was off-screen and a scroll fired;
   *  false when the element was already fully visible (no scroll
   *  needed) or no element resolved. Editor uses this to skip a
   *  repaint round-trip when nothing changed. */
  scrolled: z.boolean(),
  /** Post-scroll bounding rect in viewport coords. Editor uses
   *  this to position a follow-up overlay accurately. Omitted on
   *  no-resolve / error paths. */
  rect: z
    .object({
      x: z.number(),
      y: z.number(),
      width: z.number(),
      height: z.number(),
    })
    .optional(),
});
export type ScrollToElementResponse = z.infer<typeof ScrollToElementResponseSchema>;

// ---- AI polish endpoint -------------------------------------------------

/** One chip to polish. We pass only the structural facts the LLM needs
 *  to write a friendlier label — never selectors or full HTML. */
export const PolishChipInputSchema = z.object({
  /** Stable id supplied by the editor; the response keys back to it so
   *  out-of-order replies stay safe. */
  id: z.string().min(1).max(64),
  /** 'click' | 'type' | 'select' | 'hover' | 'assert' | 'smart'. */
  kind: z.string().min(1).max(32),
  /** Original rule-based label ('Click "Sign in"'). The polish call
   *  rewrites this; the original survives if the LLM fails. */
  label: z.string().min(1).max(200),
  /** Element text / aria-label captured from the DOM. */
  text: z.string().optional(),
  /** Element role / smart-action stepType. */
  role: z.string().optional(),
  /** 'basic' or 'smart'. Tells the LLM which voice to use. */
  section: z.enum(['basic', 'smart']),
});
export type PolishChipInput = z.infer<typeof PolishChipInputSchema>;

export const SuggestActionsPolishRequestSchema = z.object({
  /** Page URL at scan time. Used as part of the cache key so two
   *  pages with similar buttons don't collide. */
  url: z.string().min(1).max(2048),
  chips: z.array(PolishChipInputSchema).min(1).max(16),
  /** `pending-project-knowledge` — attach compiled snapshot to polish. */
  projectId: z.string().uuid().optional(),
});
export type SuggestActionsPolishRequest = z.infer<typeof SuggestActionsPolishRequestSchema>;

export const PolishChipOutputSchema = z.object({
  id: z.string(),
  /** Rewritten label. Capped at 80 chars so a verbose LLM can't break
   *  the chip layout. */
  label: z.string().min(1).max(80),
});
export type PolishChipOutput = z.infer<typeof PolishChipOutputSchema>;

// ---------------------------------------------------------------------------
// `defer-server-side-smart-actions` — Phase 11.x server-side classifier.
//
// Today the client-side builder
// (`packages/flow-builder/src/lib/suggested-actions/build-smart-suggestions.ts`)
// emits the rule-based smart chips. This contract opens the door for
// per-project / LLM-backed detection that has to live server-side:
// the FE POSTs the page outline + projectId, the server returns chips
// that match the `SmartSuggestedAction` shape the FE already knows.
//
// v1 ships the contract + a rule-based replica of the client builder
// + a Postgres cache. v2 will add LLM-backed rules (gated separately).
// ---------------------------------------------------------------------------

/**
 * Mirrors the client-side `SmartSuggestedAction` shape so the FE can
 * merge a server response into the existing chip pipeline without an
 * adapter pass. The optional `provenance` field is set to `'server'`
 * on every response so the strip can render a distinct tooltip.
 */
export const SmartChipStepTypeSchema = z.enum([
  'login',
  'fill-form',
  'search',
  'checkout',
  'verify-page',
  'api-request',
]);
export type SmartChipStepType = z.infer<typeof SmartChipStepTypeSchema>;

export const SmartChipProvenanceSchema = z.enum(['client', 'server']);
export type SmartChipProvenance = z.infer<typeof SmartChipProvenanceSchema>;

export const ServerSmartChipSchema = z.object({
  id: z.string().min(1).max(120),
  section: z.literal('smart'),
  kind: z.literal('smart'),
  stepType: SmartChipStepTypeSchema,
  label: z.string().min(1).max(120),
  /** Optional source elements. Server can omit them when the
   *  classifier doesn't have one to point at (e.g. project-scoped
   *  rules that don't tie to a specific element). The FE renders
   *  fine without them — it'll display the chip but the
   *  `+ Insert step` flow won't have a pre-picked target. */
  matchedForm: OutlineFormSchema.optional(),
  matchedElement: OutlineElementSchema.optional(),
  matchedLink: OutlineLinkSchema.optional(),
  /** Pre-filled `step.fields` payload — same shape the client
   *  builder produces. */
  fields: z.record(z.string()).default({}),
  /** Always `'server'` on a classify response. */
  provenance: SmartChipProvenanceSchema.default('server'),
});
export type ServerSmartChip = z.infer<typeof ServerSmartChipSchema>;

export const SuggestActionsClassifyHintsSchema = z.object({
  hasOpenApiLink: z.boolean().optional(),
  isMobileWebMode: z.boolean().optional(),
  isCheckoutLikePage: z.boolean().optional(),
});
export type SuggestActionsClassifyHints = z.infer<typeof SuggestActionsClassifyHintsSchema>;

export const SuggestActionsClassifyRequestSchema = z.object({
  /** UUID required so the classifier can apply per-project rules.
   *  Without it, the endpoint returns the empty-array baseline. */
  projectId: z.string().min(1).max(120),
  url: z.string().min(1).max(2048),
  /** The outline pieces the FE already collects for the polish
   *  endpoint. */
  outline: z.object({
    elements: z.array(OutlineElementSchema).default([]),
    forms: z.array(OutlineFormSchema).default([]),
    links: z.array(OutlineLinkSchema).default([]),
  }),
  hints: SuggestActionsClassifyHintsSchema.optional(),
});
export type SuggestActionsClassifyRequest = z.infer<typeof SuggestActionsClassifyRequestSchema>;

export const SuggestActionsClassifyAuditSchema = z.object({
  /** `'rule'` for v1; `'llm'` placeholder for v2. */
  source: z.enum(['rule', 'llm']),
  durationMs: z.number().int().nonnegative(),
  cacheHit: z.boolean(),
});
export type SuggestActionsClassifyAudit = z.infer<typeof SuggestActionsClassifyAuditSchema>;

export const SuggestActionsClassifyResponseSchema = z.object({
  chips: z.array(ServerSmartChipSchema),
  /** Hex SHA-256 of `(projectId + outline fingerprint)`. The FE
   *  may surface it for support — never required for correctness. */
  cacheKey: z.string().min(1).max(128),
  audit: SuggestActionsClassifyAuditSchema,
});
export type SuggestActionsClassifyResponse = z.infer<typeof SuggestActionsClassifyResponseSchema>;

export const SuggestActionsPolishResponseSchema = z.object({
  /** Adapter that served the response: 'openai' | 'groq' | 'anthropic'
   *  | 'rule' (deterministic fallback when no LLM is configured). */
  provider: z.string(),
  /** True when served from the in-process LRU cache. */
  cached: z.boolean(),
  chips: z.array(PolishChipOutputSchema),
});
export type SuggestActionsPolishResponse = z.infer<typeof SuggestActionsPolishResponseSchema>;

// ---- Telemetry endpoint -------------------------------------------------

/**
 * The two recorded action codes. Audit-log search panel already
 * supports filtering on `action`, so admins can answer "what was the
 * accept rate this week?" without new tooling.
 */
export const SuggestActionsTelemetryActionSchema = z.enum([
  'flow.suggest.shown',
  'flow.suggest.accepted',
  // `defer-server-side-smart-actions` (Phase 11.x) — observability
  // for the server-classifier surface. `requested` fires on every
  // classify call (cache miss only) so operators can chart spend;
  // `degraded` fires when the FE has to fall back to client-only on
  // timeout / 5xx / schema-violation.
  'flow.suggest.classify.requested',
  'flow.suggest.classify.degraded',
  // `pending-suggestion-panel-v3-canary` v0 (2026-05-29) — fires
  // exactly once per user the first time the editor mounts with
  // `layoutVersion === 'v3'` so the rollout is auditable in the
  // existing telemetry pipeline. Honoured only when
  // `noCode.suggestActions.emitCanaryTelemetry` is true.
  'flow.suggest.layout.v3.first_mount',
]);
export type SuggestActionsTelemetryAction = z.infer<typeof SuggestActionsTelemetryActionSchema>;

export const SuggestActionsTelemetryEventSchema = z.object({
  action: SuggestActionsTelemetryActionSchema,
  /** Page URL at telemetry time. */
  url: z.string().min(1).max(2048),
  /** Per-scan or per-click payload. Discriminated by `action`. */
  payload: z
    .object({
      // shown
      basicCount: z.number().int().nonnegative().optional(),
      smartCount: z.number().int().nonnegative().optional(),
      /**
       * Phase 11.2 — count of always-on control-flow chips rendered.
       * Today this is a constant `2` (`if-condition` + `loop-fixed`)
       * but we record it explicitly so a future change to the
       * builder shows up in the audit log without a schema diff.
       */
      controlFlowCount: z.number().int().nonnegative().optional(),
      polishApplied: z.boolean().optional(),
      /**
       * Phase 11.4–11.5 — which suggestion-panel layout served the
       * chips. `v1` = legacy flat chip strip (default). `v2` =
       * grouped element-row layout (Smart first, regional grouping,
       * verb buttons per element). `v3` = quick-picks + browse-all
       * modal (top 5 context-aware rows + searchable modal for the
       * long tail). Lets admins A/B compare adoption and
       * accept-rate across layouts via the existing audit log
       * before promoting one globally. Optional + defaults to `v1`
       * so legacy events stay valid.
       */
      layoutVersion: z.enum(['v1', 'v2', 'v3']).optional(),
      /**
       * Phase 11.4 — number of distinct element rows rendered under
       * the `v2` layout (i.e. one entry per `source.selector` after
       * verb collapse). Recorded alongside `basicCount` so the
       * collapse ratio (`basicCount / elementRowCount`) is visible
       * in the audit-log without a second event. Omitted on `v1`
       * events; the absence of this field is itself the v1 signal.
       */
      elementRowCount: z.number().int().nonnegative().optional(),
      /**
       * Phase 11.4 — number of regional groups rendered under `v2`
       * (Main / Header / Footer / etc.). Useful for spotting pages
       * with zero usable landmarks (a single "Other" bucket means
       * the runner couldn't resolve any landmark and grouping
       * collapsed). Omitted on `v1`.
       */
      regionCount: z.number().int().nonnegative().optional(),
      /**
       * Phase 11.5 — number of Quick Picks rendered inline on a
       * `v3` `flow.suggest.shown` event (capped at 5 by the
       * ranker). Recorded so admins can spot pages where the
       * ranker fell short (zero / very few picks) without opening
       * the editor. Omitted on `v1` and `v2`.
       */
      quickPickCount: z.number().int().nonnegative().max(5).optional(),
      /**
       * Phase 11.5 — `true` when the user clicked the
       * `Browse all N elements →` button on this scan (i.e.
       * escaped from Quick Picks to the full modal). Fired as a
       * separate `flow.suggest.shown` event with this flag set;
       * the original "shown" event for the scan still rides
       * without the flag. Lets us measure how often Quick Picks
       * is "good enough" vs how often users need the long tail.
       * Omitted on `v1` and `v2`.
       */
      browseAllOpened: z.boolean().optional(),
      // accepted
      /**
       * Phase 11.2 — extended with `'control-flow'`. The audit panel
       * filter list now reads `basic` / `smart` / `control-flow`; the
       * pre-Phase-11.2 wire shape stays backwards-compatible since
       * the new enum value is purely additive.
       */
      section: z.enum(['basic', 'smart', 'control-flow']).optional(),
      chipKind: z.string().max(32).optional(),
      suggestedActionType: z.string().max(64).optional(),
      confidence: z.number().min(0).max(100).optional(),
      /**
       * Phase 11.1 — when the chip was accepted via drag-drop onto an
       * `InsertButton` slot, this is the index the new step was inserted
       * after (0 = inserted between step 0 and step 1). Omitted for the
       * click path, which always inserts at the tail. Lets admins
       * distinguish click vs. drag adoption without a separate event
       * name and `flow.suggest.shown` rows ignore it.
       */
      afterIndex: z.number().int().nonnegative().optional(),
      /**
       * `defer-server-side-smart-actions` (Phase 11.x) — classify
       * event payload. Selector / element text NEVER serialised here;
       * the audit row carries only counts + reason.
       */
      projectId: z.string().max(120).optional(),
      outlineSize: z.number().int().nonnegative().optional(),
      cacheHit: z.boolean().optional(),
      durationMs: z.number().int().nonnegative().optional(),
      reason: z.enum(['timeout', 'http_error', 'schema_error', 'kill_switch']).optional(),
    })
    .default({}),
});
export type SuggestActionsTelemetryEvent = z.infer<typeof SuggestActionsTelemetryEventSchema>;

// ---- Element highlight (hover preview) ---------------------------------

/**
 * No-code program · suggested-actions hover preview.
 *
 * When the user hovers a chip in `SuggestionStrip`, the editor asks
 * the runner to outline the matching element directly inside the
 * live browser. The runner paints an absolutely-positioned overlay
 * over the element's bounding box; the JPEG stream already carries
 * that overlay back to the editor for free. On chip-leave the editor
 * sends a clear message so the page returns to its pristine state.
 *
 * The runner stores zero state between calls — every highlight
 * replaces the previous one, and every clear is idempotent.
 * Failures are silent (`ok: false` + an error string) because the
 * preview is non-critical UX; the user can still click the chip.
 */
export const HighlightElementRequestSchema = z.object({
  type: z.literal('highlight-element'),
  /** Stable selector for the element to outline. The runner does
   *  NOT score selectors here — it trusts the editor to pass back
   *  the same `selector` an earlier `outline-result` already
   *  scored. Capped at 2048 chars so a malicious / busted editor
   *  can't ship an unbounded blob through `page.evaluate`. */
  selector: z.string().min(1).max(2048),
  /** Optional id so the editor can correlate concurrent hovers
   *  if the strip starts firing them eagerly. */
  requestId: z.string().optional(),
});
export type HighlightElementRequest = z.infer<typeof HighlightElementRequestSchema>;

export const HighlightClearRequestSchema = z.object({
  type: z.literal('highlight-clear'),
  requestId: z.string().optional(),
});
export type HighlightClearRequest = z.infer<typeof HighlightClearRequestSchema>;

export const HighlightResultSchema = z.object({
  type: z.literal('highlight-result'),
  requestId: z.string().optional(),
  ok: z.boolean(),
  /** When `ok: false`, the human-readable reason. The editor logs
   *  this to devtools but does NOT surface a banner — the preview
   *  is best-effort. */
  error: z.string().optional(),
  /** Whether an overlay is currently painted on the live page after
   *  this message. `false` after a successful `highlight-clear` or
   *  when the selector matched zero elements. */
  active: z.boolean(),
});
export type HighlightResult = z.infer<typeof HighlightResultSchema>;

// NB: `PublicNoCodeSuggestActionsSchema` lives in `public-config.ts`
// alongside the other public flag schemas. Import it from there if
// you need it.
